<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatDistanceToNow, fromUnixTime } from 'date-fns';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';

const props = defineProps({
  companies: { type: Array, required: true },
  selectedCompanyIds: { type: Array, default: () => [] },
  activeSort: { type: String, default: 'name' },
  activeOrdering: { type: String, default: '' },
});

const emit = defineEmits([
  'showCompany',
  'toggleCompany',
  'toggleAll',
  'update:sort',
]);

const { t } = useI18n();

const selectedIdsSet = computed(() => new Set(props.selectedCompanyIds));
const allCompaniesSelected = computed(
  () =>
    props.companies.length > 0 &&
    props.companies.every(company => selectedIdsSet.value.has(company.id))
);

const updateSort = sort => {
  const order =
    props.activeSort === sort && props.activeOrdering === '' ? '-' : '';
  emit('update:sort', { sort, order });
};

const sortIcon = sort => {
  if (props.activeSort !== sort) return 'i-lucide-chevrons-up-down';
  return props.activeOrdering === '-'
    ? 'i-lucide-chevron-down'
    : 'i-lucide-chevron-up';
};

const formatActivity = timestamp => {
  if (!timestamp) return t('COMPANIES.TABLE.NO_ACTIVITY');
  return formatDistanceToNow(fromUnixTime(timestamp), { addSuffix: true });
};

const formatCreatedAt = timestamp => {
  if (!timestamp) return t('COMPANIES.TABLE.UNKNOWN_DATE');
  return formatDistanceToNow(fromUnixTime(timestamp), { addSuffix: true });
};
</script>

<template>
  <div class="overflow-x-auto rounded-lg border border-n-weak bg-n-surface-1">
    <table class="w-full min-w-[900px] table-fixed border-collapse">
      <thead class="bg-n-alpha-1">
        <tr class="border-b border-n-weak">
          <th class="w-12 px-4 py-3 text-start">
            <Checkbox
              :model-value="allCompaniesSelected"
              :aria-label="$t('COMPANIES.TABLE.SELECT_ALL')"
              @change="event => emit('toggleAll', event.target.checked)"
            />
          </th>
          <th class="w-[29%] px-3 py-3 text-start">
            <button
              type="button"
              class="inline-flex items-center gap-1.5 text-xs font-medium text-n-slate-10 hover:text-n-slate-12"
              @click="updateSort('name')"
            >
              {{ $t('COMPANIES.TABLE.COMPANY') }}
              <span class="size-3.5" :class="sortIcon('name')" />
            </button>
          </th>
          <th class="w-[25%] px-3 py-3 text-start">
            <button
              type="button"
              class="inline-flex items-center gap-1.5 text-xs font-medium text-n-slate-10 hover:text-n-slate-12"
              @click="updateSort('domain')"
            >
              {{ $t('COMPANIES.TABLE.DOMAIN') }}
              <span class="size-3.5" :class="sortIcon('domain')" />
            </button>
          </th>
          <th class="w-[15%] px-3 py-3 text-start">
            <button
              type="button"
              class="inline-flex items-center gap-1.5 text-xs font-medium text-n-slate-10 hover:text-n-slate-12"
              @click="updateSort('contacts_count')"
            >
              {{ $t('COMPANIES.TABLE.CONTACTS') }}
              <span class="size-3.5" :class="sortIcon('contacts_count')" />
            </button>
          </th>
          <th class="w-[17%] px-3 py-3 text-start">
            <button
              type="button"
              class="inline-flex items-center gap-1.5 text-xs font-medium text-n-slate-10 hover:text-n-slate-12"
              @click="updateSort('last_activity_at')"
            >
              {{ $t('COMPANIES.TABLE.LAST_ACTIVITY') }}
              <span class="size-3.5" :class="sortIcon('last_activity_at')" />
            </button>
          </th>
          <th class="w-[14%] px-3 py-3 text-start">
            <button
              type="button"
              class="inline-flex items-center gap-1.5 text-xs font-medium text-n-slate-10 hover:text-n-slate-12"
              @click="updateSort('created_at')"
            >
              {{ $t('COMPANIES.TABLE.CREATED') }}
              <span class="size-3.5" :class="sortIcon('created_at')" />
            </button>
          </th>
          <th class="w-12 px-3 py-3">
            <span class="sr-only">{{ $t('COMPANIES.TABLE.ACTIONS') }}</span>
          </th>
        </tr>
      </thead>
      <tbody class="divide-y divide-n-weak">
        <tr
          v-for="company in companies"
          :key="company.id"
          class="group h-16 transition-colors hover:bg-n-alpha-1"
          :class="{ 'bg-n-brand/5': selectedIdsSet.has(company.id) }"
        >
          <td class="px-4 py-2">
            <Checkbox
              :model-value="selectedIdsSet.has(company.id)"
              :aria-label="
                $t('COMPANIES.TABLE.SELECT_COMPANY', { name: company.name })
              "
              @change="
                event =>
                  emit('toggleCompany', {
                    id: company.id,
                    value: event.target.checked,
                  })
              "
            />
          </td>
          <td class="px-3 py-2">
            <button
              type="button"
              class="flex w-full min-w-0 items-center gap-3 text-start"
              @click="emit('showCompany', company.id)"
            >
              <Avatar
                :name="company.name || t('COMPANIES.UNNAMED')"
                :src="company.avatarUrl"
                :size="36"
                hide-offline-status
              />
              <span class="min-w-0">
                <span
                  class="block truncate text-sm font-medium text-n-slate-12 group-hover:text-n-blue-11"
                >
                  {{ company.name || t('COMPANIES.UNNAMED') }}
                </span>
                <span class="block truncate text-xs text-n-slate-10">
                  {{ $t('COMPANIES.TABLE.COMPANY_ID', { id: company.id }) }}
                </span>
              </span>
            </button>
          </td>
          <td class="px-3 py-2">
            <div class="flex min-w-0 items-center gap-1.5 text-n-slate-11">
              <span class="i-lucide-globe size-3.5 shrink-0" />
              <span class="truncate text-sm" :title="company.domain">
                {{ company.domain || $t('COMPANIES.TABLE.NO_DOMAIN') }}
              </span>
            </div>
          </td>
          <td class="px-3 py-2">
            <div class="flex items-center gap-1.5 text-sm text-n-slate-11">
              <span class="i-lucide-users size-3.5 shrink-0" />
              <span>{{ Number(company.contactsCount || 0) }}</span>
            </div>
          </td>
          <td class="px-3 py-2 text-sm text-n-slate-11">
            {{ formatActivity(company.lastActivityAt) }}
          </td>
          <td class="px-3 py-2 text-sm text-n-slate-11">
            {{ formatCreatedAt(company.createdAt) }}
          </td>
          <td class="px-3 py-2 text-end">
            <Button
              icon="i-lucide-chevron-right"
              slate
              xs
              ghost
              :aria-label="
                $t('COMPANIES.TABLE.VIEW_COMPANY', { name: company.name })
              "
              @click="emit('showCompany', company.id)"
            />
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
